// MA02 조회 파라미터 → 서버 요청 계획/클라 필터 — 순수 로직 (useGetProductManagementList 에서 소비, 테스트 대상)
// BE 필터 파라미터는 단수 제약(LOGISPRD-9612 toFilterCondition) — 다건 입력은 값별 요청 확장으로 서버 필터를 보존한다.
import type { ProductManagementParams } from './useGetProductManagementList'

export type SearchQuery = Record<string, unknown>

const one = (list?: string[]) => (list && list.length === 1 ? list[0] : undefined)

/**
 * BE 파라미터가 단수인 다건 입력 필드 — 확장 대상 선정 우선순위 순.
 * 선택도가 높은(값 하나가 좁은 결과를 만드는) 필드를 먼저 확장해야 요청당 응답이 작아진다:
 * optionCode(eq, 행 단위) > barcode(사실상 행 단위) > styleNumber(eq, 스타일 단위)
 * > itemCode > brandName(브랜드 단위) > shipperCode(화주 단위 — 가장 넓음)
 */
const MULTI_FIELDS: { key: keyof ProductManagementParams & string; param: string }[] = [
  { key: 'optionCodes', param: 'optionCode' },
  { key: 'barcodes', param: 'barcode' },
  { key: 'styleNumbers', param: 'styleNumber' },
  { key: 'itemCodes', param: 'itemCode' },
  { key: 'brandNames', param: 'brandName' },
  { key: 'shipperCodes', param: 'shipperCode' },
]

const values = (params: ProductManagementParams, key: (typeof MULTI_FIELDS)[number]['key']) =>
  (params[key] ?? undefined) as string[] | undefined

/** 조회/엑셀 공용 서버 파라미터 (필터 시맨틱 동일 — BE toFilterCondition). 단건 필드만 싣는다 */
export const buildSearchQuery = (params: ProductManagementParams): SearchQuery => ({
  shipperCode: one(params.shipperCodes),
  serviceTypes: params.serviceTypes?.length ? params.serviceTypes : undefined,
  brandName: one(params.brandNames),
  styleNumber: one(params.styleNumbers),
  itemCode: one(params.itemCodes),
  barcode: one(params.barcodes),
  optionCode: one(params.optionCodes),
})

/**
 * 조회용 서버 요청 쿼리 목록.
 * - 전 필드 단건이면 기존과 동일한 1건.
 * - 다건 필드가 있으면 그중 선택도가 가장 높은 1개를 골라 값별로 서버 필터를 건 요청을 값 수만큼 만든다
 *   (응답은 itemId 로 병합·중복 제거 — dedupeByItemId). 페이지 상한(size)만큼만 받고 끝나는
 *   단일 무필터 요청과 달리, 값마다 서버 필터가 걸려 페이지 경계 누락이 생기지 않는다.
 * - 확장되지 않은 나머지 다건 필드는 서버 파라미터가 단수라 실을 수 없어 클라 필터(matchesClientFilters)로 거른다.
 * - 단건 서버 파라미터(shipperCode/serviceTypes 등)는 어떤 경우에도 생략하지 않는다.
 */
export const buildServerQueries = (params: ProductManagementParams): SearchQuery[] => {
  const base = buildSearchQuery(params)
  const expand = MULTI_FIELDS.find((f) => (values(params, f.key)?.length ?? 0) > 1)
  if (!expand) return [base]
  return (values(params, expand.key) ?? []).map((value) => ({ ...base, [expand.param]: value }))
}

/** 단건은 서버 필터가 걸려 있어 통과, 다건만 IN 검사 */
export const inList = (v: string, list?: string[]) => !list || list.length <= 1 || list.includes(v)

/** 단건은 서버 필터(contains)가 걸려 있어 통과, 다건만 contains-OR 검사 */
export const containsAny = (v: string, list?: string[]) =>
  !list || list.length <= 1 || list.some((q) => v.toLowerCase().includes(q.toLowerCase()))

/** 클라 필터 대상 필드만 추린 응답 행 형태 (MasterProductItem 부분집합) */
export interface ClientFilterableItem {
  shipperCode: string
  barcode: string
  brandName?: string | null
  styleNumber?: string | null
  itemCode?: string | null
  optionCode?: number | null
}

/** 서버에 싣지 못한 다건 필드 필터 — 확장 필드에도 적용되지만 이미 서버가 거른 뒤라 무해 */
export const matchesClientFilters = (i: ClientFilterableItem, params: ProductManagementParams): boolean =>
  inList(i.shipperCode, params.shipperCodes) &&
  containsAny(i.brandName ?? '', params.brandNames) &&
  inList(i.styleNumber ?? '', params.styleNumbers) &&
  containsAny(i.itemCode ?? '', params.itemCodes) &&
  containsAny(i.barcode, params.barcodes) &&
  inList(String(i.optionCode ?? ''), params.optionCodes)

/** 값별 요청 병합 — 같은 행이 여러 요청에 걸릴 수 있어 itemId 로 중복 제거(첫 등장 우선) */
export const dedupeByItemId = <T extends { itemId: number }>(items: T[]): T[] => {
  const seen = new Set<number>()
  return items.filter((i) => (seen.has(i.itemId) ? false : (seen.add(i.itemId), true)))
}

/**
 * 다운로드 사유 모달 입력 → 감사용 사유 문자열. 1.0 RemarkPop REMARK(`{사유코드}_{상세사유}`) 동치 —
 * ST02 BE resolveReason 과 같은 형태라 EXPORT 감사로그(remark)에서 사유코드가 유실되지 않는다.
 */
export const toDetailedReason = ({ reasonCode, reasonDetail }: { reasonCode: string; reasonDetail: string }): string =>
  `${reasonCode}_${reasonDetail}`

// ── 검색조건 저장/복원 (menuOnSession — ST09·GL11 준용) ──────────────────────────
// 마지막 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
// 저장 형태는 ProductManagementParams 그대로(_seq 제외) — Utils.qsStringify(comma) ↔ useParsedQuery(comma) 왕복.
// 다건은 항목이 하나면 파서가 문자열을 주므로 asRestoredList 로 양쪽을 모두 받는다.

/** 문자열 배열만 남긴다 — comma 파서의 단건 문자열/다건 배열 겸용, 조작된 URL(객체 등)은 버린다 */
export const asRestoredList = (v: unknown): string[] | undefined => {
  if (Array.isArray(v) && v.length && typeof v[0] !== 'object') return v.map(String)
  return typeof v === 'string' && v.trim() ? [v.trim()] : undefined
}

/**
 * URL 쿼리(파싱 결과) → 복원 검색조건. 화주 필터(화주유형/화주명 — 필수 필드)가 없으면 복원 대상이 아니다(undefined).
 * 조작된 URL 이 폼/쿼리를 깨뜨리지 않도록 타입이 맞는 값만 받는다.
 */
export const fromSearchQuery = (query: Record<string, unknown>): ProductManagementParams | undefined => {
  const serviceTypes = asRestoredList(query.serviceTypes)
  const shipperCodes = asRestoredList(query.shipperCodes)
  if (!serviceTypes && !shipperCodes) return undefined
  return {
    serviceTypes,
    shipperCodes,
    brandNames: asRestoredList(query.brandNames),
    styleNumbers: asRestoredList(query.styleNumbers),
    itemCodes: asRestoredList(query.itemCodes),
    barcodes: asRestoredList(query.barcodes),
    optionCodes: asRestoredList(query.optionCodes),
  }
}

/**
 * 복원 검색조건 → FilterArea 폼 값(필드 id/폼필드명 키). 값이 있는 필드만 싣는다(필드 기본값 보존).
 * ShipperSelect 는 typeName/nameName(logisticsServiceTypes/shipperNames)이 폼 필드명이다(consts spec).
 */
export const toFilterFormValues = (params: ProductManagementParams): Record<string, unknown> => {
  const values: Record<string, unknown> = {}
  if (params.serviceTypes?.length) values['logisticsServiceTypes'] = params.serviceTypes
  if (params.shipperCodes?.length) values['shipperNames'] = params.shipperCodes
  if (params.brandNames?.length) values['f-brdNm'] = params.brandNames
  if (params.styleNumbers?.length) values['f-stlNo'] = params.styleNumbers
  if (params.itemCodes?.length) values['f-gdsCd'] = params.itemCodes
  if (params.barcodes?.length) values['f-barcode'] = params.barcodes
  if (params.optionCodes?.length) values['f-optionCode'] = params.optionCodes
  return values
}
